import React, { lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SensorType } from '@grosync/common';
import type {
  DeviceInfo,
  DeviceStatus as ApiDeviceStatus,
  FarmInfo,
  SensorReading,
} from '@grosync/common';
import {
  AdminTopBar,
  Button,
  Sym,
  StatusPill,
  StatusDot,
  ModelTag,
  Metric,
  SectionLabel,
  type DeviceStatus,
} from '../../ui';
import { ContentToolbar } from '../../ui/ContentToolbar';
import { LoadingSpinner, PageError } from '../../components/LoadingSpinner';
import { useUIStore } from '../../store/uiStore';
import { fetchDevices } from '../../api/devices';
import { fetchFarms } from '../../api/farms';
import type { FarmMarker } from './components/NodeMap';

const NodeMap = lazy(() => import('./components/NodeMap').then((m) => ({ default: m.NodeMap })));

/* ---- 농장 상태 집계 (소속 장비 중 최악 상태) -------------------------- */
const STATUS_RANK: Record<string, number> = { ERROR: 3, WARNING: 2, OFFLINE: 1, ACTIVE: 0 };
function aggregateStatus(devs: DeviceInfo[]): ApiDeviceStatus {
  if (!devs.length) return 'OFFLINE';
  return devs.reduce<ApiDeviceStatus>(
    (worst, d) => ((STATUS_RANK[d.status] ?? 0) > (STATUS_RANK[worst] ?? 0) ? d.status : worst),
    'ACTIVE',
  );
}

/* ---- backend status → design status ----------------------------------- */
function toUiStatus(s: ApiDeviceStatus): DeviceStatus {
  switch (s) {
    case 'ACTIVE':
      return 'ok';
    case 'WARNING':
    case 'ERROR':
      return 'warn';
    case 'OFFLINE':
    default:
      return 'off';
  }
}

/* ---- legend filter chips (design value → backend statuses) ------------- */
type FilterKey = 'ALL' | 'ok' | 'warn' | 'off';
interface LegendFilter {
  key: FilterKey;
  label: string;
  color: string;
  match: (s: ApiDeviceStatus) => boolean;
}
const LEGEND_FILTERS: LegendFilter[] = [
  { key: 'ALL', label: '전체', color: 'var(--md-sys-color-on-surface-variant)', match: () => true },
  { key: 'ok', label: '정상', color: 'var(--gs-primary-50)', match: (s) => toUiStatus(s) === 'ok' },
  { key: 'warn', label: '경고', color: 'var(--gs-warning)', match: (s) => toUiStatus(s) === 'warn' },
  { key: 'off', label: '오프라인', color: '#9AA095', match: (s) => toUiStatus(s) === 'off' },
];

/* ---- sensor display metadata (tone + label + icon) -------------------- */
interface SensorTone {
  label: string;
  icon: string;
  tone: string;
}
const SENSOR_TONE: Partial<Record<SensorType, SensorTone>> = {
  [SensorType.TEMPERATURE]: { label: '온도', icon: 'thermostat', tone: '#E07B3E' },
  [SensorType.SOIL_TEMPERATURE]: { label: '지온', icon: 'device_thermostat', tone: '#E07B3E' },
  [SensorType.HUMIDITY]: { label: '습도', icon: 'water_drop', tone: '#2069C7' },
  [SensorType.SOIL_MOISTURE]: { label: '토양수분', icon: 'humidity_high', tone: '#2069C7' },
  [SensorType.CO2]: { label: 'CO₂', icon: 'co2', tone: '#178A50' },
  [SensorType.ILLUMINANCE]: { label: '조도', icon: 'light_mode', tone: '#C77B20' },
  [SensorType.EC]: { label: 'EC', icon: 'bolt', tone: '#178A50' },
  [SensorType.PH]: { label: 'pH', icon: 'science', tone: '#2069C7' },
};
function sensorTone(type: SensorType): SensorTone {
  return SENSOR_TONE[type] ?? { label: type, icon: 'sensors', tone: 'var(--md-sys-color-on-surface)' };
}

function formatReading(r: SensorReading): string {
  // 큰 조도 값은 klx로 축약
  if (r.sensorType === SensorType.ILLUMINANCE && r.value >= 1000) {
    return (r.value / 1000).toFixed(1);
  }
  return Number.isInteger(r.value) ? String(r.value) : r.value.toFixed(1);
}

function formatReadingUnit(r: SensorReading): string {
  if (r.sensorType === SensorType.ILLUMINANCE && r.value >= 1000) return 'klx';
  return r.unit;
}

function formatLastSeen(iso: string | null): string {
  if (!iso) return '기록 없음';
  return new Date(iso).toLocaleString('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const PANEL_WIDTH = 368;

export default function MapPage(): React.ReactElement {
  const navigate = useNavigate();
  const { mapStatusFilter: rawFilter, setMapStatusFilter, mapSelection, setMapSelection } =
    useUIStore();
  const statusFilter: FilterKey = (rawFilter as FilterKey | null) ?? 'ALL';

  const {
    data: rawDevices = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['devices'],
    queryFn: fetchDevices,
    refetchInterval: 30_000,
  });

  const { data: farms = [] } = useQuery({ queryKey: ['farms'], queryFn: fetchFarms });

  if (isLoading) return <LoadingSpinner />;
  if (error) return <PageError message={(error as Error).message} />;

  // farmId → 소속 장비 목록
  const devicesByFarm = new Map<string, DeviceInfo[]>();
  for (const d of rawDevices) {
    if (!d.farmId) continue;
    const arr = devicesByFarm.get(d.farmId);
    if (arr) arr.push(d);
    else devicesByFarm.set(d.farmId, [d]);
  }

  const activeFilter = LEGEND_FILTERS.find((f) => f.key === statusFilter) ?? LEGEND_FILTERS[0];

  // 좌표 있는 농장만 마커로. 상태는 소속 장비 최악 상태로 집계.
  const farmMarkersAll: FarmMarker[] = farms
    .filter((f) => f.location)
    .map((f) => {
      const devs = devicesByFarm.get(f.id) ?? [];
      return {
        id: f.id,
        name: f.name,
        lat: f.location!.lat,
        lng: f.location!.lng,
        status: aggregateStatus(devs),
        deviceCount: devs.length,
        icon: f.icon ?? 'agriculture',
      };
    });

  const farmMarkers: FarmMarker[] =
    statusFilter === 'ALL'
      ? farmMarkersAll
      : farmMarkersAll.filter((f) => activeFilter.match(f.status as ApiDeviceStatus));

  const farmById = new Map<string, FarmInfo>(farms.map((f) => [f.id, f]));

  // 현재 선택 → 지도에서 강조할 농장 ID 집합
  let selectedFarmIds: string[] = [];
  if (mapSelection.kind === 'farms') {
    selectedFarmIds = mapSelection.farmIds;
  } else if (mapSelection.kind === 'device') {
    selectedFarmIds = mapSelection.fromFarmIds;
  }

  // 우측 패널에 표시할 선택 농장(좌표 유무 무관 — 클릭된 농장은 모두 표시)
  const panelFarms: FarmInfo[] =
    mapSelection.kind === 'farms'
      ? mapSelection.farmIds.map((id) => farmById.get(id)).filter((f): f is FarmInfo => !!f)
      : [];

  const selectedDevice =
    mapSelection.kind === 'device'
      ? rawDevices.find((d) => d.id === mapSelection.deviceId) ?? null
      : null;

  const handleSelectFarm = (farmIds: string[]): void =>
    setMapSelection({ kind: 'farms', farmIds });

  const handleSelectDevice = (deviceId: string, fromFarmIds: string[]): void =>
    setMapSelection({ kind: 'device', deviceId, fromFarmIds });

  const handleBackToList = (): void => {
    if (mapSelection.kind === 'device') {
      setMapSelection({ kind: 'farms', farmIds: mapSelection.fromFarmIds });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <AdminTopBar
        title="지도"
        subtitle={`전국 ${farmMarkersAll.length}개 농장 위치 · 실시간 상태`}
      />

      <div style={{ padding: '14px 28px', flexShrink: 0 }}>
        <ContentToolbar
          filters={LEGEND_FILTERS.map((lf) => {
            const active = statusFilter === lf.key;
            const count =
              lf.key === 'ALL'
                ? farmMarkersAll.length
                : farmMarkersAll.filter((f) => lf.match(f.status as ApiDeviceStatus)).length;
            return (
              <button
                key={lf.key}
                type="button"
                onClick={() => setMapStatusFilter(lf.key)}
                style={{
                  height: 38,
                  padding: '0 12px',
                  borderRadius: 9999,
                  cursor: 'pointer',
                  border: active ? 0 : '1px solid var(--md-sys-color-outline-variant)',
                  background: active ? 'var(--md-sys-color-secondary-container)' : 'transparent',
                  color: active
                    ? 'var(--md-sys-color-on-secondary-container)'
                    : 'var(--md-sys-color-on-surface-variant)',
                  font: `${active ? 600 : 500} 13px/1 var(--gs-font-kr)`,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'background 150ms',
                }}
              >
                <span
                  style={{ width: 8, height: 8, borderRadius: 9999, background: lf.color }}
                />
                {lf.label}
                <span className="gs-num" style={{ fontWeight: 700 }}>
                  {count}
                </span>
              </button>
            );
          })}
        />
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* MAP — keep the real Leaflet NodeMap */}
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          <Suspense fallback={<LoadingSpinner />}>
            <NodeMap
              farms={farmMarkers}
              selectedFarmIds={selectedFarmIds}
              onSelectFarm={handleSelectFarm}
            />
          </Suspense>
        </div>

        {/* DETAIL PANEL */}
        <aside
          className="gs-scroll-hide"
          style={{
            width: PANEL_WIDTH,
            flexShrink: 0,
            overflowY: 'auto',
            borderLeft: '1px solid var(--md-sys-color-outline-variant)',
            background: 'var(--md-sys-color-surface)',
          }}
        >
          {selectedDevice ? (
            <DeviceDetail
              device={selectedDevice}
              onBack={handleBackToList}
              onGoToData={() => navigate(`/data?deviceId=${selectedDevice.id}`)}
            />
          ) : panelFarms.length > 0 ? (
            <FarmDeviceList
              farms={panelFarms}
              devicesByFarm={devicesByFarm}
              onSelectDevice={(id) => handleSelectDevice(id, selectedFarmIds)}
            />
          ) : (
            <div
              style={{
                padding: '60px 24px',
                textAlign: 'center',
                color: 'var(--md-sys-color-on-surface-variant)',
              }}
            >
              <Sym name="touch_app" size={40} />
              <div style={{ font: '500 14px/20px var(--gs-font-kr)', marginTop: 10 }}>
                지도에서 농장을 선택하세요
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

/* ---- 선택 농장들의 장비 목록 (클러스터=다중 농장 그룹) ----------------- */
function FarmDeviceList({
  farms,
  devicesByFarm,
  onSelectDevice,
}: {
  farms: FarmInfo[];
  devicesByFarm: Map<string, DeviceInfo[]>;
  onSelectDevice: (deviceId: string) => void;
}): React.ReactElement {
  const totalDevices = farms.reduce((n, f) => n + (devicesByFarm.get(f.id)?.length ?? 0), 0);

  return (
    <div style={{ padding: '22px 22px 32px', display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* 헤더 */}
      <div>
        <SectionLabel style={{ marginBottom: 4 }}>
          {farms.length > 1 ? `농장 ${farms.length}곳` : farms[0]?.name ?? '농장'}
        </SectionLabel>
        <div
          style={{
            font: '700 18px/24px var(--gs-font-kr)',
            color: 'var(--md-sys-color-on-surface)',
          }}
        >
          장비 {totalDevices}대
        </div>
      </div>

      {farms.map((farm) => {
        const devs = devicesByFarm.get(farm.id) ?? [];
        return (
          <div key={farm.id}>
            {/* 농장 그룹 헤더 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Sym
                name={farm.icon ?? 'agriculture'}
                size={18}
                fill={1}
                style={{ color: 'var(--md-sys-color-primary)' }}
              />
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  font: '600 14px/18px var(--gs-font-kr)',
                  color: 'var(--md-sys-color-on-surface)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {farm.name}
              </span>
              <span
                className="gs-num"
                style={{
                  font: '500 12px/1 var(--gs-font-kr)',
                  color: 'var(--md-sys-color-on-surface-variant)',
                }}
              >
                {devs.length}대
              </span>
            </div>

            {/* 장비 행 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {devs.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => onSelectDevice(d.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '12px 14px',
                    borderRadius: 14,
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    background: 'var(--md-sys-color-surface-container-low)',
                    border: '1px solid var(--md-sys-color-outline-variant)',
                    transition: 'background 150ms',
                  }}
                >
                  <StatusDot s={toUiStatus(d.status)} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        font: '600 13px/17px var(--gs-font-kr)',
                        color: 'var(--md-sys-color-on-surface)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {d.name}
                    </div>
                    <div
                      className="gs-num"
                      style={{
                        font: '400 11px/14px var(--gs-font-kr)',
                        color: 'var(--md-sys-color-on-surface-variant)',
                        marginTop: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {d.id}
                    </div>
                  </div>
                  <ModelTag m={d.model ?? 1} />
                  <Sym
                    name="chevron_right"
                    size={18}
                    style={{ color: 'var(--md-sys-color-on-surface-variant)' }}
                  />
                </button>
              ))}
              {devs.length === 0 && (
                <div
                  style={{
                    padding: '16px 14px',
                    borderRadius: 14,
                    background: 'var(--md-sys-color-surface-container-low)',
                    border: '1px solid var(--md-sys-color-outline-variant)',
                    font: '400 13px/18px var(--gs-font-kr)',
                    color: 'var(--md-sys-color-on-surface-variant)',
                    textAlign: 'center',
                  }}
                >
                  등록된 장비가 없습니다
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---- detail panel for a selected device -------------------------------- */
function DeviceDetail({
  device,
  onBack,
  onGoToData,
}: {
  device: DeviceInfo;
  onBack: () => void;
  onGoToData: () => void;
}): React.ReactElement {
  const ui = toUiStatus(device.status);
  const readings = device.latestReadings ?? [];

  return (
    <div style={{ padding: '22px 22px 32px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* back to list */}
      <button
        type="button"
        onClick={onBack}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          alignSelf: 'flex-start',
          padding: '6px 10px 6px 6px',
          marginBottom: -4,
          borderRadius: 9999,
          cursor: 'pointer',
          background: 'transparent',
          border: 0,
          font: '600 13px/1 var(--gs-font-kr)',
          color: 'var(--md-sys-color-on-surface-variant)',
        }}
      >
        <Sym name="arrow_back" size={18} />
        목록으로
      </button>

      {/* identity */}
      <div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 14,
              flexShrink: 0,
              background: 'var(--md-sys-color-primary-container)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Sym
              name="sensors"
              size={26}
              fill={1}
              style={{ color: 'var(--md-sys-color-on-primary-container)' }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                font: '700 19px/24px var(--gs-font-kr)',
                color: 'var(--md-sys-color-on-surface)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {device.name}
            </div>
            <div
              className="gs-num"
              style={{
                font: '400 12px/16px var(--gs-font-kr)',
                color: 'var(--md-sys-color-on-surface-variant)',
                marginTop: 3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {device.id}
            </div>
          </div>
          <StatusPill s={ui} />
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 14, alignItems: 'center' }}>
          <ModelTag m={device.model ?? 1} />
          {device.cropType && <MetaChip icon="potted_plant" text={device.cropType} />}
        </div>
      </div>

      {/* farm / location */}
      <div>
        <SectionLabel style={{ marginBottom: 10 }}>위치 정보</SectionLabel>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            padding: '14px 16px',
            borderRadius: 14,
            background: 'var(--md-sys-color-surface-container-low)',
            border: '1px solid var(--md-sys-color-outline-variant)',
          }}
        >
          <LocationRow icon="agriculture" label="농장">
            {device.farmId ?? '미할당'}
          </LocationRow>
          <LocationRow icon="location_on" label="위치">
            {device.location?.description ??
              (device.location
                ? `${device.location.lat.toFixed(5)}, ${device.location.lng.toFixed(5)}`
                : '위치 미설정')}
          </LocationRow>
          <LocationRow icon="schedule" label="최근 수신">
            {formatLastSeen(device.lastSeenAt)}
          </LocationRow>
        </div>
      </div>

      {/* live sensor metrics */}
      <div>
        <SectionLabel style={{ marginBottom: 10 }}>실시간 센서</SectionLabel>
        {ui !== 'off' && readings.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {readings.map((r) => {
              const t = sensorTone(r.sensorType);
              return (
                <Metric
                  key={r.sensorType}
                  icon={t.icon}
                  label={t.label}
                  v={formatReading(r)}
                  u={formatReadingUnit(r)}
                  tone={t.tone}
                  warn={r.anomaly !== 'normal'}
                />
              );
            })}
          </div>
        ) : (
          <div
            style={{
              padding: 16,
              borderRadius: 14,
              textAlign: 'center',
              background: 'var(--md-sys-color-surface-container-low)',
              border: '1px solid var(--md-sys-color-outline-variant)',
              color: 'var(--md-sys-color-on-surface-variant)',
              font: '400 12px/18px var(--gs-font-kr)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Sym name={ui === 'off' ? 'signal_disconnected' : 'sensors_off'} size={20} />
            {ui === 'off'
              ? `오프라인 · 마지막 수신 ${formatLastSeen(device.lastSeenAt)}`
              : '수신된 센서 데이터가 없습니다'}
          </div>
        )}
      </div>

      <Button variant="tonal" icon="bar_chart" onClick={onGoToData} style={{ height: 44, width: '100%' }}>
        데이터 조회
      </Button>
    </div>
  );
}

/* ---- small helpers ----------------------------------------------------- */
function MetaChip({ icon, text }: { icon: string; text: string }): React.ReactElement {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: 28,
        padding: '0 12px',
        borderRadius: 9999,
        background: 'var(--md-sys-color-surface-container-high)',
        font: '500 12px/1 var(--gs-font-kr)',
        color: 'var(--md-sys-color-on-surface-variant)',
      }}
    >
      <Sym name={icon} size={14} />
      {text}
    </span>
  );
}

function LocationRow({
  icon,
  label,
  children,
}: {
  icon: string;
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <Sym name={icon} size={16} style={{ color: 'var(--md-sys-color-on-surface-variant)' }} />
      <span
        style={{
          width: 56,
          font: '400 12px/1 var(--gs-font-kr)',
          color: 'var(--md-sys-color-on-surface-variant)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          flex: 1,
          textAlign: 'right',
          font: '500 12px/1.4 var(--gs-font-kr)',
          color: 'var(--md-sys-color-on-surface)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {children}
      </span>
    </div>
  );
}
